/**
 * Acceptance-criteria gate — turns agent-self-declared "done" into an objective,
 * Stop-hook-enforceable check, with no AI involved.
 *
 * `runVerify` only runs generic project-health phases (typecheck, build, adapters,
 * drift). It has no task-specific notion of what "done" means. This module reads a
 * `<!-- cai:accept-start -->` … `<!-- cai:accept-end -->` block (consistent with the
 * cai:start/end and cai:learn-start/end markers) containing deterministic predicates,
 * and evaluates each one.
 *
 * Predicate grammar (one per line, blank lines and `#` comments ignored):
 *   file-exists: <path>            — path exists on disk (relative to project root)
 *   no-file:     <path>            — path does NOT exist
 *   grep:        <pattern> in <glob>   — at least one file matching <glob> contains <pattern>
 *   grep:        <pattern>         — <pattern> appears anywhere under the project (excludes node_modules/.git/dist)
 *   cmd:         <shell command>   — command exits 0 (run through the user's shell so pipes/quotes work)
 *   no-drift                       — `cai check` reports zero drift errors
 *
 * Design rules: deterministic, no AI, no network. Reads only inside the markers.
 * `cmd:` and `grep:` get a real parser — they are NOT a naive whitespace split,
 * because they legitimately carry pipes, quotes, and glob metacharacters.
 */
import { existsSync, readFileSync } from "node:fs";
import { spawnSync } from "node:child_process";
import { join } from "node:path";
import { globSync } from "glob";
import type { CaiConfig } from "./types.js";
import type { VerifyStep } from "./verify.js";

const ACCEPT_START = "<!-- cai:accept-start -->";
const ACCEPT_END = "<!-- cai:accept-end -->";

const DEFAULT_TIMEOUT_MS = 60_000;
const IGNORE_GLOBS = ["**/node_modules/**", "**/.git/**", "**/dist/**", "**/target/**"];

export interface AcceptPredicate {
  raw: string;
  kind: "file-exists" | "no-file" | "grep" | "cmd" | "no-drift";
  /** Parsed argument payload (path, pattern, command, etc.) */
  arg: string;
  /** For grep-in-glob: the glob to scope the search to. */
  glob?: string;
}

export interface AcceptResult {
  /** True when the block was found at all. */
  found: boolean;
  steps: VerifyStep[];
  passed: boolean;
}

/** Default location of the acceptance block. */
export function defaultAcceptPath(config: CaiConfig): string {
  return join(config.scaffoldRoot, "ACCEPTANCE.md");
}

/** Extract the text between the accept markers, or null when the block is absent. */
export function extractAcceptBlock(content: string): string | null {
  const startIdx = content.indexOf(ACCEPT_START);
  const endIdx = content.indexOf(ACCEPT_END);
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return null;
  return content.slice(startIdx + ACCEPT_START.length, endIdx).trim();
}

/** Parse the predicate lines inside an accept block. Unknown lines are skipped. */
export function parsePredicates(block: string): AcceptPredicate[] {
  const predicates: AcceptPredicate[] = [];

  for (const rawLine of block.split("\n")) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;

    if (line === "no-drift") {
      predicates.push({ raw: line, kind: "no-drift", arg: "" });
      continue;
    }

    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) continue;
    const directive = line.slice(0, colonIdx).trim();
    const payload = line.slice(colonIdx + 1).trim();
    if (!payload) continue;

    switch (directive) {
      case "file-exists":
        predicates.push({ raw: line, kind: "file-exists", arg: payload });
        break;
      case "no-file":
        predicates.push({ raw: line, kind: "no-file", arg: payload });
        break;
      case "cmd":
        // Keep the payload verbatim — it is a shell command with its own quoting.
        predicates.push({ raw: line, kind: "cmd", arg: payload });
        break;
      case "grep": {
        // Split on the last " in " so the pattern may itself contain " in ".
        const inIdx = payload.lastIndexOf(" in ");
        if (inIdx !== -1) {
          predicates.push({
            raw: line,
            kind: "grep",
            arg: payload.slice(0, inIdx).trim(),
            glob: payload.slice(inIdx + 4).trim(),
          });
        } else {
          predicates.push({ raw: line, kind: "grep", arg: payload });
        }
        break;
      }
      // unknown directive — skip silently
    }
  }

  return predicates;
}

function step(name: string, command: string, ok: boolean, start: number, output?: string): VerifyStep {
  return {
    name,
    command,
    status: ok ? "passed" : "failed",
    durationMs: Date.now() - start,
    output: ok ? undefined : output,
  };
}

function evalGrep(pred: AcceptPredicate, projectRoot: string, start: number): VerifyStep {
  const cmd = pred.glob ? `grep: ${pred.arg} in ${pred.glob}` : `grep: ${pred.arg}`;
  let needle: RegExp;
  try {
    needle = new RegExp(pred.arg);
  } catch {
    // Treat an invalid regex as a literal substring.
    needle = new RegExp(pred.arg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  }

  const glob = pred.glob ?? "**/*";
  let files: string[];
  try {
    files = globSync(glob, { cwd: projectRoot, ignore: IGNORE_GLOBS, nodir: true });
  } catch {
    return step("accept:grep", cmd, false, start, `invalid glob: ${glob}`);
  }

  for (const rel of files) {
    try {
      const content = readFileSync(join(projectRoot, rel), "utf8");
      if (needle.test(content)) return step("accept:grep", cmd, true, start);
    } catch {
      // unreadable / binary — skip
    }
  }
  return step("accept:grep", cmd, false, start, `no file matching "${glob}" contains /${pred.arg}/`);
}

function evalCmd(pred: AcceptPredicate, projectRoot: string, timeoutMs: number, start: number): VerifyStep {
  // Run through the shell so pipes, quotes, and redirects work as written.
  const res = spawnSync(pred.arg, {
    cwd: projectRoot,
    shell: true,
    encoding: "utf8",
    timeout: timeoutMs,
    stdio: ["ignore", "pipe", "pipe"],
    maxBuffer: 4 * 1024 * 1024,
  });
  if (res.error) {
    const code = (res.error as NodeJS.ErrnoException).code;
    return {
      name: "accept:cmd",
      command: `cmd: ${pred.arg}`,
      status: code === "ENOENT" ? "skipped" : "failed",
      durationMs: Date.now() - start,
      output: res.error.message,
    };
  }
  const ok = res.status === 0;
  const combined = ((res.stdout ?? "") + "\n" + (res.stderr ?? "")).trim();
  return step("accept:cmd", `cmd: ${pred.arg}`, ok, start, combined.slice(0, 2000));
}

/**
 * Evaluate the acceptance block at `acceptPath` (or config default).
 * Returns found=false when no block is present so callers can decide whether
 * the absence is a hard failure or a no-op.
 */
export async function runAccept(
  config: CaiConfig,
  opts: { acceptPath?: string; timeoutMs?: number } = {}
): Promise<AcceptResult> {
  const path = opts.acceptPath ?? defaultAcceptPath(config);
  const timeout = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  if (!existsSync(path)) return { found: false, steps: [], passed: true };

  const block = extractAcceptBlock(readFileSync(path, "utf8"));
  if (block === null) return { found: false, steps: [], passed: true };

  const predicates = parsePredicates(block);
  const steps: VerifyStep[] = [];

  for (const pred of predicates) {
    const start = Date.now();
    switch (pred.kind) {
      case "file-exists": {
        const ok = existsSync(join(config.projectRoot, pred.arg));
        steps.push(step("accept:file-exists", `file-exists: ${pred.arg}`, ok, start, `not found: ${pred.arg}`));
        break;
      }
      case "no-file": {
        const ok = !existsSync(join(config.projectRoot, pred.arg));
        steps.push(step("accept:no-file", `no-file: ${pred.arg}`, ok, start, `should not exist: ${pred.arg}`));
        break;
      }
      case "grep":
        steps.push(evalGrep(pred, config.projectRoot, start));
        break;
      case "cmd":
        steps.push(evalCmd(pred, config.projectRoot, timeout, start));
        break;
      case "no-drift": {
        const { runDriftCheck } = await import("./drift/index.js");
        try {
          const report = await runDriftCheck(config, { skip: ["staleness"] });
          const errors = report.issues.filter((i) => i.severity === "error").length;
          steps.push(step("accept:no-drift", "no-drift", errors === 0, start, `${errors} drift error(s) (score ${report.score}/100)`));
        } catch (err) {
          steps.push(step("accept:no-drift", "no-drift", false, start, (err as Error).message));
        }
        break;
      }
    }
  }

  return { found: true, steps, passed: steps.every((s) => s.status !== "failed") };
}
